import { useMemo, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Paper, Typography, useTheme } from "@mui/material";
import { useChartPalette } from "../utils/chartUtils";
import { EmptyChartState } from "@/components/common/EmptyChartState";

type RankPoint = {
  month: string;
  carrier: string;
  avg_days: number;
  reimbursement_count: number;
  speed_rank: number;
};

type Props = {
  data: RankPoint[];
  isFullscreen: boolean;
};

type BumpPayloadItem = { value?: number; dataKey?: string; color?: string };

type BumpTooltipProps = {
  active?: boolean;
  label?: string | number;
  payload?: BumpPayloadItem[];
  avgLookup: Map<string, { avg: number; count: number }>;
};

const BumpTooltip = ({ active, payload, label, avgLookup }: BumpTooltipProps) => {
  if (!active || !payload?.length) return null;
  const rows = [...payload]
    .filter((p) => p.value !== undefined && p.value !== null)
    .sort((a, b) => Number(a.value) - Number(b.value));
  return (
    <Paper elevation={3} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {rows.map((r) => {
        const info = avgLookup.get(`${label}|${r.dataKey}`);
        return (
          <Typography key={r.dataKey} variant="body2" sx={{ color: r.color }}>
            #{r.value} {r.dataKey}
            {info ? ` — ${info.avg.toFixed(1)} days (${info.count})` : ""}
          </Typography>
        );
      })}
    </Paper>
  );
};

/**
 * Bump/rank chart: month-over-month carrier reimbursement speed. Each line is a
 * carrier; the y-axis is their speed rank that month (1 = fastest, at the top).
 * Lines "bump" as carriers overtake each other. Built in recharts (inverted rank
 * axis) to stay consistent with the rest of the dashboard — no nivo dependency.
 */
const CarrierReimbursementBumpChart = ({ data, isFullscreen }: Props) => {
  const palette = useChartPalette();
  const theme = useTheme();

  const [hoveredCarrier, setHoveredCarrier] = useState<string | null>(null);

  const { rows, carriers, maxRank, avgLookup } = useMemo(() => {
    const carriers = Array.from(new Set(data.map((d) => d.carrier)));
    const months = Array.from(new Set(data.map((d) => d.month))).sort();
    const avgLookup = new Map<string, { avg: number; count: number }>();
    data.forEach((d) => avgLookup.set(`${d.month}|${d.carrier}`, { avg: d.avg_days, count: d.reimbursement_count }));
    const rows = months.map((m) => {
      const row: Record<string, string | number> = { month: m };
      carriers.forEach((c) => {
        const pt = data.find((d) => d.month === m && d.carrier === c);
        if (pt) row[c] = pt.speed_rank;
      });
      return row;
    });
    const maxRank = data.reduce((mx, d) => Math.max(mx, d.speed_rank), 1);
    return { rows, carriers, maxRank, avgLookup };
  }, [data]);

  if (!data.length) return <EmptyChartState />;

  const ticks = Array.from({ length: maxRank }, (_, i) => i + 1);

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? 500 : "100%"}>
      <LineChart data={rows} margin={{ top: 10, right: 24, left: 10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.palette.divider} />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        />
        <YAxis
          reversed
          domain={[1, maxRank]}
          ticks={ticks}
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
          label={{
            value: "Rank (1 = fastest)",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 11, fill: theme.palette.text.secondary },
          }}
        />
        <Tooltip
          content={(props) => (
            <BumpTooltip
              active={props.active}
              label={props.label}
              payload={props.payload as unknown as BumpPayloadItem[] | undefined}
              avgLookup={avgLookup}
            />
          )}
        />
        <Legend
          wrapperStyle={{ fontSize: 12 }}
          onMouseEnter={(o) => setHoveredCarrier((o as { value?: string }).value ?? null)}
          onMouseLeave={() => setHoveredCarrier(null)}
        />
        {carriers.map((c, i) => {
          const dimmed = hoveredCarrier !== null && hoveredCarrier !== c;
          return (
            <Line
              key={c}
              type="monotone"
              dataKey={c}
              name={c}
              stroke={palette[i % palette.length]}
              strokeWidth={hoveredCarrier === c ? 4 : 3}
              strokeOpacity={dimmed ? 0.15 : 1}
              connectNulls
              dot={{ r: 4, fillOpacity: dimmed ? 0.15 : 1, strokeOpacity: dimmed ? 0.15 : 1 }}
              activeDot={{ r: 6 }}
              onMouseEnter={() => setHoveredCarrier(c)}
              onMouseLeave={() => setHoveredCarrier(null)}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default CarrierReimbursementBumpChart;
